import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePageMeta } from '../lib/meta';
import { Alert, Button } from '../components/ui';

/**
 * Order lookup.
 *
 * Shoppers have no accounts — guest checkout is the only customer path — so
 * this replaces an order history page. Anyone returning to an order does it
 * with the two details on their confirmation email.
 *
 * If customer accounts arrive later they will be application accounts held in
 * this system's own database, not identities in a Microsoft directory. Nothing
 * here needs to change for that: this page becomes an additional route in
 * rather than a replacement for it.
 */
export function AccountPage() {
  const navigate = useNavigate();
  const { principal } = useAuth();

  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  usePageMeta({
    title: 'Find your order | Gill’s Handmade',
    description: 'Look up an order using your order number and email address.',
  });

  async function lookup(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { order } = await api.lookupOrder(orderNumber, email);
      navigate(`/orders/${order.id}?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'We could not look that up just now. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-container max-w-md py-16">
      <h1 className="text-3xl">Find your order</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted">
        Enter the order number and email address from your confirmation email.
        There is no account to create — every order can be placed as a guest.
      </p>

      <form className="mt-8 space-y-4" onSubmit={lookup}>
        <div>
          <label
            htmlFor="order-number"
            className="mb-1.5 block text-xs uppercase tracking-widest text-muted"
          >
            Order number
          </label>
          <input
            id="order-number"
            required
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            placeholder="GH-1042"
            className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-ink"
          />
        </div>

        <div>
          <label
            htmlFor="order-email"
            className="mb-1.5 block text-xs uppercase tracking-widest text-muted"
          >
            Email address
          </label>
          <input
            id="order-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-ink"
          />
        </div>

        {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Find order
        </Button>
      </form>

      <p className="mt-8 text-center text-xs text-muted">
        <Link to="/collections/all-jewellery" className="underline underline-offset-4">
          Back to the jewellery
        </Link>
      </p>

      {/* Staff only. Shown once signed in so there is a way back to admin;
          the sign-in itself lives at /admin. */}
      {principal?.isAdmin && (
        <p className="mt-10 border-t border-border pt-6 text-center text-xs">
          <Link to="/admin" className="underline underline-offset-4">
            Store admin
          </Link>
        </p>
      )}
    </div>
  );
}
